<template>
  <div class="substep-invite-members">
    <div class="substep-header">
      <h3 data-testId="step-title">Get your team set up</h3>
      <p>Invite your team to start tracking and unlock full visibility.</p>
    </div>
    <div class="substep-body">
      <div class="invite-column">
        <div class="invite-via-email-section">
          <h4 class="invite-section-title">Invite your team via email</h4>
          <div class="embedded-form-box">
            <div class="embedded-form-box-body">
              <label class="control-label">EMAIL</label>
              <div
                v-for="(invite, index) in newInvites"
                :key="index"
                class="field-line"
              >
                <div class="input-combo">
                  <AppInput
                    v-model="invite.email"
                    placeholder="username@example.com"
                    type="email"
                  />
                  <AppSelect
                    v-model="invite.role"
                    :async="false"
                    :options="roles"
                    :select2-options="select2Options"
                    placeholder="Select a role"
                  />
                </div>
                <div class="errors">
                  {{ errorMessages[index] }}
                </div>
              </div>
            </div>
            <div>
              <a
                v-if="canAddInvites"
                href="#"
                class="add-member-link"
                @click="addInvite($event)"
              >
                + Add another member
              </a>
            </div>
            <div class="embedded-form-box-footer">
              <AppButton
                class="btn-primary"
                data-testid="send-invites-button"
                :disabled="showLoading"
                @click="inviteMembers"
              >
                Send invites
              </AppButton>
            </div>
          </div>
        </div>
        <div class="invite-via-link-section">
          <h4 class="invite-section-title">Invite your team via link</h4>
          <div class="app-copy-input-value">
            <div class="input-group">
              <input
                ref="inputValue"
                class="form-control"
                :value="inviteUrl"
                readonly
                @click="selectInputText"
              />
              <span
                class="input-group-addon"
                :class="{ pressed: btnPressed, disabled: disableCopy }"
                @click.prevent="copyInputValue"
              >
                <a
                  href="#"
                  class="copy-link"
                  data-testid="copy-invite-link-button"
                >{{ btnText }}</a>
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="hasInvites"
          class="members-section"
        >
          <h4 class="invite-section-title">Invites sent</h4>
          <div class="invites-table">
            <div class="invites-table-header">
              <span class="invites-table-email">Email</span>
              <span class="invites-table-role">Role</span>
            </div>
            <div
              v-for="invite in invites"
              :key="invite.id"
              class="invites-table-row"
            >
              <span class="invites-table-email">{{ invite.email }}</span>
              <span class="invites-table-role">
                <span class="role-pill">{{ formatRole(invite.role) }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="resources-column">
        <div class="video-section">
          <div class="resources-column-title">Hubstaff quickstart guide for new users</div>
          <div class="resources-column-description">This video shows what your team will see.</div>
          <a
            :href="quickstartVideoUrl"
            class="video-section-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              :src="GETTING_STARTED_QUICKSTART_VIDEO_IMG"
              alt="Hubstaff quickstart guide for new users"
            />
          </a>
        </div>
        <div class="toolkit-section">
          <div class="resources-column-title">Get your team familiar with Hubstaff</div>
          <div class="resources-column-description">
            Check out the 👉
            <a
              :href="employeeEngagementToolkitUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              Hubstaff employee engagement toolkit
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GettingStartedMixin from '~/mixins/GettingStarted/GettingStartedMixin';
import AppInput from '~/components/AppInput.vue';
import AppButton from '~/components/AppButton.vue';
import { InvitesResource } from '~/resources';
import { GETTING_STARTED_QUICKSTART_VIDEO_IMG } from '~/rails_assets';

export default {
  name: 'InviteMembers',
  components: {
    AppButton,
    AppInput,
  },
  mixins: [GettingStartedMixin],
  props: {
    invites: {
      type: Array,
      default: () => [],
      validator: (invites) => {
        return invites.every((invite) => {
          return !!invite.id && !!invite.email && typeof invite.role === 'number';
        });
      },
    },
    organization: {
      type: Object,
      required: true,
      validator: (organization) => {
        return !!organization.id;
      },
    },
    inviteUrl: {
      type: String,
      default: '',
    },
    quickstartVideoUrl: {
      type: String,
      default: '',
    },
    employeeEngagementToolkitUrl: {
      type: String,
      default: '',
    },
    inviteSource: {
      type: String,
      default: 'Getting Started Invite Members',
    },
    reloadSubstep: {
      type: Function,
      required: true,
    },
    completeSubstep: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      GETTING_STARTED_QUICKSTART_VIDEO_IMG,
      newInvites: [{ email: '', role: 0 }],
      showLoading: false,
      errorMessages: [],
      btnPressed: false,
      roles: [
        { id: '0', text: 'User' },
        { id: '2', text: 'Manager' },
      ],
      select2Options: Object.freeze({
        allowClear: false,
        multiple: false,
      }),
    };
  },
  computed: {
    hasInvites() {
      return this.invites.length > 0;
    },
    canAddInvites() {
      return this.newInvites.length < 20;
    },
    disableCopy() {
      return !this.inviteUrl;
    },
    btnText() {
      return this.btnPressed ? 'Copied' : 'Copy';
    },
  },
  watch: {
    invites: {
      handler() {
        this.checkInvites();
      },
      immediate: true,
      deep: true,
    },
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
  methods: {
    async addInvite(event) {
      event.preventDefault();
      this.newInvites.push({ email: '', role: '0' });
    },
    buildInvitePayload() {
      const invite = [];
      const rowIndexes = [];

      this.newInvites.forEach((row, index) => {
        if (!row.email) {
          return;
        }

        invite.push({
          email: row.email,
          role: Number(row.role),
        });
        rowIndexes.push(index);
      });

      return { invite, rowIndexes };
    },
    mapErrorsToInviteRows(errors, rowIndexes) {
      const messages = Array(this.newInvites.length).fill('');

      Object.entries(errors || {}).forEach(([submittedIndex, message]) => {
        if (!message) {
          return;
        }

        const rowIndex = rowIndexes[Number(submittedIndex)];

        if (rowIndex !== undefined) {
          messages[rowIndex] = message;
        }
      });

      return messages;
    },
    async inviteMembers() {
      const { invite, rowIndexes } = this.buildInvitePayload();

      this.errorMessages = Array(this.newInvites.length).fill('');
      this.showLoading = true;
      const params = {
        invite,
        source: this.inviteSource,
      };

      try {
        const response = await InvitesResource.bulkCreate({ id: this.organization.id }, params, {});

        if (response.success) {
          this.newInvites = [{ email: '', role: 0 }];

          await this.reloadSubstep();
          await this.$nextTick();

          if (this.hasInvites) {
            await this.checkInvites();
          }
        } else if (response.errors) {
          this.errorMessages = this.mapErrorsToInviteRows(response.errors, rowIndexes);
        }
      } finally {
        this.showLoading = false;
      }
    },
    async checkInvites() {
      if (this.invites.length === 0) {
        return;
      }

      await this.onInviteCompleted();
    },
    async onInviteCompleted() {
      await this.completeSubstep();
    },
    formatRole(role) {
      return this.roles.find((r) => r.id === role.toString()).text;
    },
    selectInputText() {
      this.$refs.inputValue.select();
    },
    deSelectInputText() {
      this.$refs.inputValue.blur();
    },
    async copyToClipboard() {
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(this.inviteUrl);

          return;
        } catch (e) {
          // fall through to the execCommand fallback
        }
      }

      this.selectInputText();
      if (!document.execCommand('copy')) {
        throw new Error('copy failed');
      }
    },
    stopCopy() {
      this.deSelectInputText();
      this.btnPressed = false;
      clearInterval(this.interval);
    },
    async copyInputValue() {
      if (this.btnPressed || this.disableCopy) {
        return;
      }

      try {
        await this.copyToClipboard();
        this.btnPressed = true;
        this.interval = setInterval(() => {
          this.stopCopy();
        }, 3000);

        this.$emit('trackWizardEvent', 'Invite link copied', { source: 'Getting Started Invite Members' });
        await this.onInviteCompleted();
      } catch (e) {
        this.$appMessage('There was an unexpected error');
      }
    },
  },
};
</script>
